import type { RouteLocationRaw } from 'vue-router';

type NavItem = {
    /** 与中台站点导航一致：首页固定 id=1 */
    id?: number;
    moduleKey?: string;
    link?: string;
    /** 1 应用 2 链接（与中台导航约定一致） */
    link_type?: number | string;
    sub_navs?: NavItem[];
};

type ModuleUrlMap = Record<string, string>;

/** 与 router [home] 一致，避免配置指向自身导致守卫死循环 */
export const DEFAULT_IDX_HOME_PATH = '/idx/home';

function normalizePathname(pathname: string): string {
    const p = (pathname || '/').replace(/\/+$/, '') || '/';
    return p;
}

/**
 * 配置的首页目标是否就是默认门户首页（/idx/home），若是则不应再重定向。
 */
export function isDefaultHomeRouteTarget(raw: string): boolean {
    const url = String(raw || '').trim();
    if (!url) {
        return false;
    }
    const homePath = normalizePathname(DEFAULT_IDX_HOME_PATH);
    try {
        if (/^https?:\/\//i.test(url)) {
            return normalizePathname(new URL(url).pathname) === homePath;
        }
        if (url.startsWith('/')) {
            const pathOnly = url.split(/[?#]/)[0];
            return normalizePathname(pathOnly) === homePath;
        }
    } catch {
        return false;
    }
    return false;
}

/** 中台首页项：navigation.data 中与站点配置约定一致的 id=1 */
function pickPortalHomeNavItem(menus: unknown): NavItem | null {
    if (!Array.isArray(menus)) {
        return null;
    }
    for (const raw of menus) {
        const item = raw as NavItem;
        if (item?.id === 1) {
            return item;
        }
        const nested = pickPortalHomeNavItem(item?.sub_navs);
        if (nested) {
            return nested;
        }
    }
    return null;
}

/**
 * 从首页导航项解析「真实首页」跳转地址（使用 link + link_type，不再使用 homeUrl）。
 * - link_type 为 2：外链，使用 `link`（可为 https 或同域 path）。
 * - link_type 为 1：应用，优先使用 moduleKey 从 getModuleUrl 返回值里取 URL。
 * - 其它情况仅当 `link` 形如路径或 http(s) 时使用，避免把展示文案误当链接。
 */
export function pickHomeNavTargetFromHomeNavItem(
    item: NavItem | null | undefined,
    moduleUrlMap: ModuleUrlMap = {},
): string {
    if (!item) {
        return '';
    }
    const link = typeof item.link === 'string' ? item.link.trim() : '';
    const lt = item.link_type;
    const isAppType = lt === 1 || lt === '1';
    const isLinkTypeUrl = lt === 2 || lt === '2';
    const looksLikeUrl = link.startsWith('/') || /^https?:\/\//i.test(link);

    let target = '';
    if (isLinkTypeUrl) {
        target = link;
    } else if (isAppType && item.moduleKey && item.moduleKey !== 'home') {
        target = moduleUrlMap[item.moduleKey] || '';
    } else if (looksLikeUrl) {
        target = link;
    }
    if (target && isDefaultHomeRouteTarget(target)) {
        return '';
    }
    return target;
}

/**
 * 从 getNavContent → navigation.data 解析出的菜单树中，读取首页项的跳转目标。
 */
export function pickHomeNavUrlFromMenus(menus: unknown, moduleUrlMap: ModuleUrlMap = {}): string {
    return pickHomeNavTargetFromHomeNavItem(pickPortalHomeNavItem(menus), moduleUrlMap);
}

/**
 * 将配置的首页目标地址转为：本 SPA 路由跳转，或整页跳转（站外 / 同域非 /idx 应用）。
 * @returns 路由对象或 path 字符串 → 交给 vue-router；`false` → 已整页跳转并应取消本次导航；`null` → 不处理
 */
export function resolveHomeNavUrlForGuard(raw: string): RouteLocationRaw | false | null {
    const url = String(raw || '').trim();
    if (!url) {
        return null;
    }
    if (isDefaultHomeRouteTarget(url)) {
        return null;
    }
    if (/^https?:\/\//i.test(url)) {
        window.location.assign(url);
        return false;
    }
    if (url.startsWith('/idx')) {
        return url;
    }
    if (url.startsWith('/')) {
        window.location.assign(`${window.location.origin}${url}`);
        return false;
    }
    window.location.assign(url);
    return false;
}
